import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import objectsService, { LocationUpdate } from '../services/ObjectsService';
import { TrackedObject } from '../types';
import { useAuth } from '../context/AuthContext';
import { eventEmitter, EVENTS } from '../events/EventEmitter';
import { mapServiceObjectToAppObject, mapServiceObjectsToAppObjects } from '../utils/objectMappers';
import '../styles/TestTools.css';

// Component to handle map clicks for selecting locations
const MapClickHandler: React.FC<{
  onLocationSelected: (lat: number, lng: number) => void;
}> = ({ onLocationSelected }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelected(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const TestTools: React.FC = () => {
  const { authState } = useAuth();
  const [objects, setObjects] = useState<TrackedObject[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedObjectId, setSelectedObjectId] = useState<string>('');
  const [latitude, setLatitude] = useState<number>(37.7749);
  const [longitude, setLongitude] = useState<number>(-122.4194);
  const [newObjectName, setNewObjectName] = useState<string>('');
  const [newObjectDescription, setNewObjectDescription] = useState<string>('');
  const [iconUrl, setIconUrl] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isCreatingObject, setIsCreatingObject] = useState<boolean>(false);
  const [isUpdatingLocation, setIsUpdatingLocation] = useState<boolean>(false);

  useEffect(() => {
    fetchObjects();
  }, []);

  const fetchObjects = async () => {
    setLoading(true);
    try {
      const serviceObjects = await objectsService.getAllObjects();
      
      // Get locations for each object
      const locationMap = new Map<string, LocationUpdate>();
      await Promise.all(serviceObjects.map(async (obj) => {
        try {
          const location = await objectsService.getLatestLocation(obj.id);
          if (location) {
            locationMap.set(obj.id, location);
          }
        } catch (e) {
          console.error(`Failed to get location for object ${obj.id}:`, e);
        }
      }));
      
      // Convert service objects to app objects
      const appObjects = mapServiceObjectsToAppObjects(serviceObjects, locationMap);
      
      setObjects(appObjects);
      
      if (appObjects.length > 0 && !selectedObjectId) {
        setSelectedObjectId(appObjects[0].id);
      }
    } catch (error) {
      console.error('Error fetching objects:', error);
      setError('Failed to fetch objects. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSelected = (lat: number, lng: number) => {
    setLatitude(lat);
    setLongitude(lng);
  };

  const handleCreateObject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newObjectName.trim()) {
      setError('Object name is required');
      return;
    }

    setIsCreatingObject(true);
    setError('');
    setMessage('');

    try {
      // Create the object in the service
      const serviceObject = await objectsService.createObject({
        name: newObjectName,
        description: newObjectDescription || undefined,
        iconUrl: iconUrl || undefined,
      });
      
      // Convert to app object format
      const location = await objectsService.getLatestLocation(serviceObject.id);
      const appObject = mapServiceObjectToAppObject(serviceObject, location);
      
      // Update state with the new object
      setObjects([...objects, appObject]);
      setSelectedObjectId(serviceObject.id);
      setNewObjectName('');
      setNewObjectDescription('');
      setIconUrl('');
      setMessage(`Object "${serviceObject.name}" created successfully!`);
      
      // Switch to location update form
      setIsCreatingObject(false);
    } catch (error) {
      console.error('Error creating object:', error);
      setError('Failed to create object. Check console for details.');
      setIsCreatingObject(false);
    }
  };

  const handleUpdateLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedObjectId) {
      setError('Please select an object');
      return;
    }

    setIsUpdatingLocation(true);
    setError('');
    setMessage('');

    try {
      // Update location
      const update = await objectsService.updateObjectLocation(
        selectedObjectId,
        latitude,
        longitude
      );
      
      // If in development mode, explicitly update the mock location cache
      if (objectsService.isDevMode()) {
        objectsService.updateMockLocation(selectedObjectId, latitude, longitude);
      }
      
      // Emit location update event to notify other components
      const selectedObject = objects.find(obj => obj.id === selectedObjectId);
      if (selectedObject) {
        const updatedObject: TrackedObject = {
          ...selectedObject,
          latitude,
          longitude,
          lastUpdate: update.timestamp
        };
        
        // Emit event with the updated object data
        eventEmitter.emit(EVENTS.LOCATION_UPDATED, updatedObject);
      }
      
      setMessage(`Location updated for object ID: ${update.objectId}`);
      setIsUpdatingLocation(false);
    } catch (error) {
      console.error('Error updating location:', error);
      setError('Failed to update location. Check console for details.');
      setIsUpdatingLocation(false);
    }
  };

  // If not authenticated, show login message
  if (!authState.isAuthenticated) {
    return (
      <div className="test-tools">
        <h2>Test Tools</h2>
        <p>Please log in to use the test tools.</p>
      </div>
    );
  }

  return (
    <div className="test-tools">
      <h2>Test Tools</h2>
      
      {/* Display messages */}
      {message && <div className="message success">{message}</div>}
      {error && <div className="message error">{error}</div>}
      
      {/* Toggle between creating object and updating location */}
      <div className="action-toggle">
        <button 
          className={isCreatingObject ? 'active' : ''} 
          onClick={() => setIsCreatingObject(true)}
        >
          Create New Object
        </button>
        <button 
          className={!isCreatingObject ? 'active' : ''} 
          onClick={() => setIsCreatingObject(false)}
        >
          Update Location
        </button>
      </div>
      
      {/* Create Object Form */}
      {isCreatingObject && (
        <div className="create-object-form">
          <h3>Create New Object</h3>
          <form onSubmit={handleCreateObject}>
            <div className="form-group">
              <label htmlFor="object-name">Object Name:</label>
              <input
                id="object-name"
                type="text"
                value={newObjectName}
                onChange={(e) => setNewObjectName(e.target.value)}
                placeholder="Enter object name"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="object-description">Description (optional):</label>
              <textarea
                id="object-description"
                value={newObjectDescription}
                onChange={(e) => setNewObjectDescription(e.target.value)}
                placeholder="Enter object description"
                rows={3}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="icon-url">Custom Icon URL (optional):</label>
              <input
                id="icon-url"
                type="text"
                value={iconUrl}
                onChange={(e) => setIconUrl(e.target.value)}
                placeholder="Enter URL to custom icon image"
              />
              <small className="form-hint">
                Example: https://example.com/icon.png (40x40 pixels recommended)
              </small>
            </div>
            
            <button type="submit" disabled={loading || !newObjectName.trim()}>
              Create Object
            </button>
          </form>
        </div>
      )}
      
      {/* Update Location Form */}
      {!isCreatingObject && (
        <div className="update-location-form">
          <h3>Update Object Location</h3>
          
          {loading ? (
            <p>Loading objects...</p>
          ) : (
            <form onSubmit={handleUpdateLocation}>
              {/* Object selection dropdown */}
              <div className="form-group">
                <label htmlFor="object-select">Select Object:</label>
                <select
                  id="object-select"
                  value={selectedObjectId}
                  onChange={(e) => setSelectedObjectId(e.target.value)}
                  required
                >
                  <option value="">-- Select an object --</option>
                  {objects.map((obj) => (
                    <option key={obj.id} value={obj.id}>
                      {obj.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Coordinate inputs */}
              <div className="coordinates">
                <div className="form-group">
                  <label htmlFor="latitude">Latitude:</label>
                  <input
                    id="latitude"
                    type="number"
                    step="any"
                    value={latitude}
                    onChange={(e) => setLatitude(parseFloat(e.target.value))}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="longitude">Longitude:</label>
                  <input
                    id="longitude"
                    type="number"
                    step="any"
                    value={longitude}
                    onChange={(e) => setLongitude(parseFloat(e.target.value))}
                    required
                  />
                </div>
              </div>
              
              {/* Map for selecting location */}
              <div className="location-map-container">
                <p>Click on the map to set coordinates:</p>
                <div className="location-map">
                  <MapContainer
                    center={[latitude, longitude]}
                    zoom={13}
                    scrollWheelZoom={true}
                    style={{ height: '300px', width: '100%' }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <Marker position={[latitude, longitude]}></Marker>
                    <MapClickHandler onLocationSelected={handleLocationSelected} />
                  </MapContainer>
                </div>
              </div>
              
              <button type="submit" disabled={!selectedObjectId}>
                Update Location
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default TestTools;
